import { Box } from '@byecode/ui'
import type { FieldType } from '@lighthouse/core'
import { getFieldIcon, IGNORE_WATCH_NODE_FIELD_TYPES, useAtomAction } from '@lighthouse/shared'
import { find } from 'rambda'
import React, { useCallback, useMemo } from 'react'
import { useUpdateEffect } from 'react-use'

import { getDataSourceMetaAtom } from '@/atoms/dataSource/action'
import { useCurrentAppId, useCurrentEnvId } from '@/hooks/useApplication'
import { useDataSource } from '@/hooks/useDataSource'
import { useIsDisabledWithVersion } from '@/hooks/useIsDisabledWithVersion'

import { GroupRequired } from '../../Common/GroupRequired'
import * as SC from '../../styles'

interface AttentionLabelProps {
    num?: number
    max: number
    onToggleAllCheck?: () => void
}

interface CheckBoxLabelProps {
    label: string
    icon: string
}

interface FieldList {
    label: string
    value: string
    type: FieldType
    icon: string
}

interface AttentionFieldProps {
    dataSourceId: string
    value: string[]
    onChange: (value: string[]) => void
}

const AttentionLabel: React.FC<AttentionLabelProps> = ({ num = 0, max, onToggleAllCheck }) => {
    const disabledWithVersion = useIsDisabledWithVersion()
    return (
        <SC.FlexBetween>
            <SC.Title>已关注 {num}</SC.Title>
            <SC.Check
                checked={num > 0}
                disabled={disabledWithVersion}
                indeterminate={num > 0 && max > num}
                label="全选"
                onChange={onToggleAllCheck}
            />
        </SC.FlexBetween>
    )
}

const CheckBoxLabel: React.FC<CheckBoxLabelProps> = ({ label, icon }) => {
    return (
        <SC.FlexStart>
            <SC.Icon size={16} type={icon} />
            <SC.Title>{label}</SC.Title>
        </SC.FlexStart>
    )
}

export const AttentionField: React.FC<AttentionFieldProps> = ({ dataSourceId, value, onChange }) => {
    const appId = useCurrentAppId()
    const envId = useCurrentEnvId()
    const dataSource = useDataSource(appId, envId, dataSourceId)
    const { run: getDataSourceMeta } = useAtomAction(getDataSourceMetaAtom)
    const disabledWithVersion = useIsDisabledWithVersion()
    const watchedFields: string[] = value

    useUpdateEffect(() => {
        getDataSourceMeta({ envId, dsId: dataSourceId })
        onChange([])
    }, [dataSourceId, envId])

    const fieldsList = useMemo(() => {
        if (!dataSource) {
            return []
        }
        const {
            schema,
            viewOptions: { tableProps }
        } = dataSource
        const newTableProps = tableProps.reduce<FieldList[]>((list, cur) => {
            const field = schema[cur.id]
            if (field && !IGNORE_WATCH_NODE_FIELD_TYPES.has(field.type)) {
                list.push({
                    label: field.name,
                    value: field.id,
                    type: field.type,
                    icon: getFieldIcon(field.id, field.type, field.innerType)
                })
            }
            return list
        }, [])

        return Object.values(schema).reduce((prev, cur) => {
            const field = find(item => item.value === cur.id, prev)
            if (!field && !IGNORE_WATCH_NODE_FIELD_TYPES.has(cur.type)) {
                prev.push({
                    label: cur.name,
                    value: cur.id,
                    type: cur.type,
                    icon: getFieldIcon(cur.id, cur.type, cur.innerType)
                })
            }
            return prev
        }, newTableProps)
    }, [dataSource])

    const watchedFieldIds = useMemo(() => {
        if (!dataSource) {
            return []
        }
        const { schema } = dataSource
        return watchedFields?.filter(id => {
            const field = schema[id]
            return field && !IGNORE_WATCH_NODE_FIELD_TYPES.has(field.type)
        })
    }, [dataSource, watchedFields])

    const watchIds = useMemo(() => {
        if (!dataSource) {
            return
        }
        return watchedFieldIds?.filter(id => !!dataSource.schema[id])
    }, [dataSource, watchedFieldIds])

    const handleToggleAllCheck = useCallback(() => {
        const checked = watchIds?.length === fieldsList.length
        onChange(checked ? [] : fieldsList.map(item => item.value))
    }, [fieldsList, onChange, watchIds?.length])
    return (
        <SC.Container>
            <GroupRequired label="关注以下任一字段的变更">
                <Box px={8}>
                    <AttentionLabel num={watchIds?.length} max={fieldsList.length} onToggleAllCheck={handleToggleAllCheck} />
                    {(() => {
                        const schema = dataSource?.schema
                        const filteredValue = schema ? watchedFields?.filter((id: string) => !!schema[id]) : watchedFields
                        return (
                            <SC.CheckboxGroup value={filteredValue} onChange={onChange} spacing="xs" orientation="vertical">
                                {fieldsList.map(item => (
                                    <SC.CheckItem
                                        disabled={disabledWithVersion}
                                        key={item.value}
                                        value={item.value}
                                        label={<CheckBoxLabel label={item.label} icon={item.icon} />}
                                    />
                                ))}
                            </SC.CheckboxGroup>
                        )
                    })()}
                    <SC.Tip>当以上指定的其中一个字段更新时将触发流程</SC.Tip>
                </Box>
            </GroupRequired>
        </SC.Container>
    )
}
